import React from "react";
import "./PrivacyPolicyPage.css";

function PrivacyPolicyPage() {
  return (
    <main className="privacy-page">
      <div className="privacy-page-container">
        {/* Header */}
        <div className="privacy-page-header">
          <h1 className="privacy-page-title">Privacy Policy</h1>
          <p className="privacy-page-subtitle">
            Your privacy is important to us. This policy explains how we
            collect, use, and protect your information.
          </p>
          <p className="privacy-page-last-updated">Last updated: 07/23/2026</p>
        </div>

        <div className="privacy-page-content">
          {/* Navigation Options */}

          {/* Privacy Policy Content */}
          <div className="privacy-page-policy-content">
            <section className="privacy-page-policy-section">
              <h2>Information We Collect</h2>
              <div className="privacy-page-policy-text">
                <h3>Personal Information</h3>
                <p>
                  We collect information you provide directly to us, such as
                  when you create an account, use our services, or contact us
                  for support. This may include:
                </p>
                <ul>
                  <li>Name, email address, and contact information</li>
                  <li>Account credentials and authentication information</li>
                  <li>Profile information and preferences</li>
                  <li>Training progress and performance data</li>
                  <li>Communications with our support team</li>
                </ul>

                <h3>Automatically Collected Information</h3>
                <p>
                  When you use our services, we may automatically collect
                  certain information about your device and how you interact
                  with our platform:
                </p>
                <ul>
                  <li>Device information (type, operating system, browser)</li>
                  <li>IP address and location data</li>
                  <li>Usage data and analytics</li>
                  <li>Log files and error reports</li>
                </ul>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>How We Use Your Information</h2>
              <div className="privacy-page-policy-text">
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Provide, maintain, and improve our services</li>
                  <li>
                    Process your account registration and manage your account
                  </li>
                  <li>Deliver training content and track your progress</li>
                  <li>Send you important updates and notifications</li>
                  <li>
                    Provide customer support and respond to your inquiries
                  </li>
                  <li>Analyze usage patterns to improve user experience</li>
                  <li>Ensure the security and integrity of our platform</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Information Sharing and Disclosure</h2>
              <div className="privacy-page-policy-text">
                <p>
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share your information in the following
                  circumstances:
                </p>
                <ul>
                  <li>
                    <strong>With your employer:</strong> If you access Reppi
                    through your employer, we may share relevant training and
                    performance data as required for business purposes
                  </li>
                  <li>
                    <strong>Service providers:</strong> We may share information
                    with trusted third-party service providers who assist us in
                    operating our platform
                  </li>
                  <li>
                    <strong>Legal requirements:</strong> We may disclose
                    information when required by law or to protect our rights
                    and safety
                  </li>
                  <li>
                    <strong>Business transfers:</strong> Information may be
                    transferred as part of a merger, acquisition, or sale of
                    business assets
                  </li>
                </ul>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>SMS/Text Messaging</h2>
              <div className="privacy-page-policy-text">
                <p>
                  When you provide your mobile phone number through a welcome
                  survey or similar form, you consent to receive text (SMS)
                  messages from us, including a link to complete your welcome
                  survey and related follow-up messages.
                </p>
                <ul>
                  <li>
                    <strong>No mobile information sharing:</strong> No mobile
                    information will be shared with third parties or affiliates
                    for marketing or promotional purposes. Text messaging
                    originator opt-in data and consent are not shared with any
                    third parties, except with subprocessors (such as our SMS
                    provider, Twilio) solely for the purpose of delivering the
                    messages you have requested.
                  </li>
                  <li>
                    <strong>Message frequency:</strong> Message frequency
                    varies. You will typically receive a message when a welcome
                    survey link is sent to you, along with occasional related
                    follow-up messages.
                  </li>
                  <li>
                    <strong>Message and data rates:</strong> Message and data
                    rates may apply, depending on your mobile carrier and plan.
                  </li>
                  <li>
                    <strong>Opting out:</strong> You can opt out of receiving
                    text messages at any time by replying STOP to any message.
                    For help, reply HELP or contact us at{" "}
                    <a
                      href="mailto:help@reppi.co"
                      className="privacy-page-email-link"
                    >
                      help@reppi.co
                    </a>
                    .
                  </li>
                </ul>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Data Security</h2>
              <div className="privacy-page-policy-text">
                <p>
                  We implement appropriate technical and organizational measures
                  to protect your personal information against unauthorized
                  access, alteration, disclosure, or destruction. These measures
                  include:
                </p>
                <ul>
                  <li>Encryption of data in transit and at rest</li>
                  <li>Access controls and authentication requirements</li>
                  <li>Employee training on data protection practices</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or
                  electronic storage is 100% secure. While we strive to protect
                  your personal information, we cannot guarantee absolute
                  security.
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Data Retention</h2>
              <div className="privacy-page-policy-text">
                <p>
                  We retain your personal information for as long as necessary
                  to provide our services and fulfill the purposes outlined in
                  this privacy policy. We may also retain information to comply
                  with legal obligations, resolve disputes, and enforce our
                  agreements.
                </p>
                <p>
                  When you delete your account, we will delete or anonymize your
                  personal information within a reasonable timeframe, unless we
                  are required to retain it for legal reasons.
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Your Rights and Choices</h2>
              <div className="privacy-page-policy-text">
                <p>
                  Depending on your location, you may have certain rights
                  regarding your personal information:
                </p>
                <ul>
                  <li>
                    <strong>Access:</strong> Request access to your personal
                    information
                  </li>
                  <li>
                    <strong>Correction:</strong> Request correction of
                    inaccurate information
                  </li>
                  <li>
                    <strong>Deletion:</strong> Request deletion of your personal
                    information
                  </li>
                  <li>
                    <strong>Portability:</strong> Request a copy of your data in
                    a portable format
                  </li>
                  <li>
                    <strong>Opt-out:</strong> Opt out of certain communications
                    or data processing
                  </li>
                </ul>
                <p>
                  To exercise these rights, please contact us at
                  <a
                    href="mailto:help@reppi.co"
                    className="privacy-page-email-link"
                  >
                    help@reppi.co
                  </a>
                  .
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Cookies and Tracking</h2>
              <div className="privacy-page-policy-text">
                <p>
                  We use cookies and similar tracking technologies to enhance
                  your experience on our platform. Cookies help us:
                </p>
                <ul>
                  <li>Remember your login status and preferences</li>
                  <li>Analyze how you use our services</li>
                  <li>Provide personalized content and features</li>
                  <li>Improve our platform's performance and security</li>
                </ul>
                <p>
                  You can control cookie settings through your browser
                  preferences, but please note that disabling cookies may affect
                  the functionality of our services.
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Children's Privacy</h2>
              <div className="privacy-page-policy-text">
                <p>
                  Our services are not intended for individuals under the age of
                  18. We do not knowingly collect personal information from
                  children under 18. If we become aware that we have collected
                  personal information from a child under 18, we will take steps
                  to delete such information.
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>International Data Transfers</h2>
              <div className="privacy-page-policy-text">
                <p>
                  Your information may be transferred to and processed in
                  countries other than your own. We ensure that such transfers
                  comply with applicable data protection laws and that
                  appropriate safeguards are in place to protect your
                  information.
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Changes to This Policy</h2>
              <div className="privacy-page-policy-text">
                <p>
                  We may update this privacy policy from time to time to reflect
                  changes in our practices or legal requirements. We will notify
                  you of any material changes by posting the updated policy on
                  our website and updating the "Last updated" date.
                </p>
                <p>
                  Your continued use of our services after any changes indicates
                  your acceptance of the updated policy.
                </p>
              </div>
            </section>

            <section className="privacy-page-policy-section">
              <h2>Contact Us</h2>
              <div className="privacy-page-policy-text">
                <p>
                  If you have any questions about this privacy policy or our
                  data practices, please contact us at:
                </p>
                <div className="privacy-page-contact-info">
                  <p>
                    <strong>Email:</strong>{" "}
                    <a
                      href="mailto:help@reppi.co"
                      className="privacy-page-email-link"
                    >
                      help@reppi.co
                    </a>
                  </p>
                  <p>
                    <strong>Subject:</strong> Privacy Policy Inquiry
                  </p>
                </div>
                <p>
                  We will respond to your inquiry within a reasonable timeframe
                  and work to address any concerns you may have.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}

export default PrivacyPolicyPage;
